import { useEffect, useState } from 'react';
import { Button, DateRangePicker, DescriptionBar, Grid, SelectBox } from 'components';
import { cancelStatus, contactGridColumns, contactGridStatus, paymentStatus } from './utils';
import { ListType } from 'types/selectbox';
import { GroundStation, ScheduleGridData, Spacecraft } from './types';
import axios from 'common/AxiosAuth';
import DeleteModal from './DeleteModal';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import 'styles/mobile.css';
import { useTranslation } from 'react-i18next';

const ManageContacts = () => {
  const [spaceCraftFilter, setSpaceCraftFilter] = useState<number | string>('');
  const [contactFilter, setContactFilter] = useState<number | string>('');
  const [checkedRows, setCheckedRows] = useState<number[]>([]);
  const [date, setDate] = useState<string[]>([]);
  const [spacecraftData, setSpacecraftData] = useState<number | undefined>(undefined);
  const [groundstationData, setGroundstationData] = useState<number | undefined>(undefined);
  const [selectSpacecraftList, setSelectSpacecraftList] = useState<ListType[]>([]);
  const [selectGroundstationList, setSelectGroundstationList] = useState<ListType[]>([]);
  const [gridData, setGridData] = useState<ScheduleGridData[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const pageSize = 10;
  const [isDeleteModalOpened, setIsDeleteModalOpened] = useState(false);
  const [data, setData] = useState<ScheduleGridData | null>(null);
  const [loading, setLoading] = useState(false);
  const [searchFilters, setSearchFilters] = useState<{
    spacecraft?: number | string;
    groundStation?: number | string;
    date?: string[];
  } | null>(null);

  const { t } = useTranslation();

  const mapStatusToName = (statusName: string) => {
    switch (statusName) {
      case 'IDLE': return 'Idle';
      case 'SCHEDULED': return 'Scheduled';
      case 'PROCESS': return 'Progress';
      case 'COMPLETE': return 'Done';
      case 'FAIL': return 'Fail';
      case 'CANCEL': return 'Cancel';
      default: return 'Unknown';
    }
  };

  const mapCancelStatusIdToName = (cancelStatus: string) => {
    switch (cancelStatus) {
      case 'CANCEL': return 'Cancel';
      case 'TOBEREFUNDED': return 'Toberefunded';
      case 'TO_BE_REFUNDED': return 'TO_BE_REFUNDED';
      case 'REFUNDED': return 'Refunded';
      case 'NONE': return 'None';
      default: return 'unknown';
    }
  };

  const handleSpacecraftChange = (value: any) => {
    setSpaceCraftFilter(value);
    setSpacecraftData(value);
  };

  const handleGroundStationChange = (value: any) => {
    setContactFilter(value);
    setGroundstationData(value);
  };

  const handleSearchClick = () => {
    setSearchFilters({ spacecraft: spaceCraftFilter, groundStation: contactFilter, date });
    setCurrentPage(1);
  };

  const handleResetClick = () => {
    setSpaceCraftFilter('');
    setContactFilter('');
    setDate([]);
    setSpacecraftData(undefined);
    setGroundstationData(undefined);
    setSearchFilters(null);
    setCurrentPage(1);
  };

  const searchScheduleContact = async (filters: { spacecraft?: number | string; groundStation?: number | string; date?: string[] }, pageNum: number) => {
    setLoading(true);
    try {
      const params: any = {
        page: pageNum - 1,
        size: pageSize,
        sort: 'startTime,DESC',
      };
      if (filters.spacecraft) params.linkId = filters.spacecraft;
      if (filters.groundStation) params.contactProfileId = filters.groundStation;
      if (filters.date?.[0]) params.startDate = filters.date[0];
      if (filters.date?.[1]) params.endDate = filters.date[1];

      const res = await axios.get('/api/schedule/search', { params });
      const { content = [], totalElements = 0 } = res.data;

      const processedData = content.map((item: ScheduleGridData) => {
        const spacecraft = selectSpacecraftList.find(s => s.value === item.linkId);
        const groundstation = selectGroundstationList.find(g => g.value === item.contactProfileId);
        return {
          ...item,
          orderId: item.orderId,
          cancelStatus: mapCancelStatusIdToName(item.cancelStatus),
          statusId: item.statusId,
          statusName: mapStatusToName(item.status),
          MaxElevation: item.maxElevationDegrees,
          linkName: spacecraft?.label || item.linkName,
          contactProfileName: groundstation?.label || item.contactProfileName,
          availableTime: item.availableTime,
          paymentKey: item.paymentKey
        };
      });

      setGridData(processedData);
      setTotalPages(Math.ceil(totalElements / pageSize));
    } catch (error) {
      console.error('스케줄 검색 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  const getScheduleContact = async (pageNum: number) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`/api/schedule/list/${pageNum}?pageSize=${pageSize}`);
      const { scheduleList = [], totalElement = 0 } = data;
      const allScheduleData = scheduleList.map((item: ScheduleGridData) => ({
        ...item,
        id: item.linkId,
        orderId: item.orderId,
        cancelStatus: mapCancelStatusIdToName(item.cancelStatus),
        cancelable: item.cancelable,
        statusId: item.statusId,
        statusName: mapStatusToName(item.status),
        MaxElevation: item.maxElevationDegrees,
        linkName: item.linkName,
        contactProfileName: item.contactProfileName,
        availableTime: item.availableTime,
        paymentKey: item.paymentKey
      }));
      setGridData(allScheduleData);
      setTotalPages(Math.ceil(totalElement / pageSize));
    } catch (error) {
      console.error('Error fetching schedule data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (searchFilters) {
      searchScheduleContact(searchFilters, currentPage);
    } else {
      getScheduleContact(currentPage);
    }
  }, [currentPage, searchFilters, selectSpacecraftList, selectGroundstationList]);

  useEffect(() => {
    const fetchSelectData = async () => {
      try {
        const spacecraftRes = await axios.get('/api/link/listAll');
        const spacecraftOptions = spacecraftRes.data.map((item: Spacecraft) => ({ value: item.linkViewId, label: item.name }));
        setSelectSpacecraftList(spacecraftOptions);

        const groundstationRes = await axios.get('/api/contactprofile/listAll');
        const groundstationOptions = groundstationRes.data.map((item: GroundStation) => ({ value: item.contactProfileId, label: item.name }));
        setSelectGroundstationList(groundstationOptions);
      } catch (error) {
        console.error('Error fetching select options:', error);
      }
    };
    fetchSelectData();
  }, []);

  const onClickCancel = (gridData: ScheduleGridData) => {
    setData(gridData);
    setIsDeleteModalOpened(true);
  };

  const handleDeleteConfirmed = () => {
    setIsDeleteModalOpened(false);
    if (searchFilters) {
      searchScheduleContact(searchFilters, currentPage);
    } else {
      getScheduleContact(currentPage);
    }
  };

  return (
    <div className="flex h-full w-full flex-col">
      <DescriptionBar
        className="md:px-6"
        title={t('manageContacts.pageTitle')}
        description={t('manageContacts.pageDescription')}
      />
      <div className="gap-5 p-4 md:p-6 grid grid-cols-2 grid-rows-2 md:grid-cols-5 md:grid-rows-1 grid-rows-md">
        <SelectBox
          className="max-w-none"
          bgColor="layer2"
          label="Spacecraft"
          labelTooltip=""
          list={selectSpacecraftList}
          value={spacecraftData}
          setValue={handleSpacecraftChange} // 핸들러 수정
        />
        <SelectBox
          className="max-w-none"
          bgColor="layer2"
          label="Ground Station"
          labelTooltip=""
          list={selectGroundstationList}
          value={groundstationData}
          setValue={handleGroundStationChange} // 핸들러 수정
        />
        <DateRangePicker className="col-span-2" bgColor="layer2" label="Date" labelTooltip="Date" value={date} setValue={setDate} />
        <div className="flex items-end justify-start gap-2">
          <Button className="h-8" text={t('manageContacts.button.search')} type="tertiary" size="large" onClick={handleSearchClick} aria-label="search list"></Button>
          <Button className="h-8" text={t('manageContacts.button.reset')} type="tertiary" size="large" onClick={handleResetClick} aria-label="search term reset"></Button>
        </div>
      </div>
      {loading ? (
        <div className="grid grid-cols-8 gap-4 pl-4 pr-4">
          {Array(8).fill(null).map((_, index) => (
            <div key={index} className="h-12">
              {Array(8).fill(null).map((_, innerIndex) => (
                <Skeleton key={innerIndex} className="w-full" />
              ))}
            </div>
          ))}
        </div>
      ) : (
        <div className="flex w-full flex-1 flex-col justify-between">
          <Grid
            gridColumns={contactGridColumns}
            gridData={gridData}
            filterValue={{
              spacecraft: spaceCraftFilter,
              groundStation: contactFilter,
              date: date.join(' - '),
            }}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            options={{
              useCheckbox: false,
              useFilter: true,
              customCell: {
                availableTime: (_, row) => <span>{(row as ScheduleGridData).availableTime + ' sec'}</span>,
                MaxElevation: (_, row) => <span>{(row as ScheduleGridData).maxElevationDegrees + 'º'}</span>,
                statusName: (value) => {
                  const status = contactGridStatus[value];
                  if (status) {
                    return (
                      <div title={value}>
                        <div className={`flex h-6 w-fit items-center rounded-full border px-3 ${status.className}`}>
                          <span className="text-nowrap text-small font-regular">{status.title}</span>
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div className="h-12 py-0" title={value}>
                      <p className="text-text-primary font-regular text-body2 w-full overflow-hidden text-ellipsis whitespace-nowrap">
                        {value}
                      </p>
                    </div>
                  );
                },
                cancelable: (value, row) => {
                    const schedule = row as ScheduleGridData;

                    // ✨ [수정] cancelable이 true이고, cancelStatus가 'None'일 때만 버튼을 활성화하도록 조건 변경
                    const isButtonEnabled = schedule.cancelable && schedule.cancelStatus === 'None';

                    if (isButtonEnabled) {
                        // --- 활성화된 Cancel 버튼 ---
                        return (
                            <div title="Cancel">
                                <button
                                    type="button"
                                    className="cursor-pointer flex flex-row gap-1 items-center rounded border px-2 h-6 text-small font-regular bg-button-bg_secondary text-button-text_secondary border-border_primary hover:bg-secondary_hover"
                                    onClick={() => onClickCancel(schedule)}
                                >
                                    <span className="select-none text-nowrap">Cancel</span>
                                </button>
                            </div>
                        );
                    } else {
                        // --- 비활성화된 Cancel 버튼 ---
                        return (
                            <div title="Cancel">
                                <button
                                    type="button"
                                    className="cursor-not-allowed opacity-50 flex flex-row gap-1 items-center rounded border px-2 h-6 text-small font-regular bg-button-bg_secondary text-button-text_secondary border-border_primary"
                                    disabled
                                >
                                    <span className="select-none text-nowrap">Cancel</span>
                                </button>
                            </div>
                        );
                    }
                },
                cancelStatus: (value) => {
                  const status = cancelStatus[value];
                  if (status) {
                    return (
                      <div title={value}>
                        <div className={`flex h-6 w-fit items-center rounded-full border px-3 ${status.className}`}>
                          <span className="text-nowrap text-small font-regular">{status.title}</span>
                        </div>
                      </div>
                    );
                  }
                  return (
                    <div className="h-12 py-0" title={value}>
                      <p className="text-text-primary font-regular text-body2 w-full overflow-hidden text-ellipsis whitespace-nowrap">
                        {value}
                      </p>
                    </div>
                  );
                },
                isPayment: (value, row) => {
                  const status = value ? paymentStatus.paymentCompleted : paymentStatus.paymentPending;
                  return (
                    <div title={status.title}>
                      <div className={`flex h-6 w-fit items-center rounded-full border px-3 ${status.className}`}>
                        <span className="text-nowrap text-small font-regular">{status.title}</span>
                      </div>
                    </div>
                  );
                },
              },
              usePagination: {
                use: true,
                maxPage: totalPages,
              },
            }}
            checkedRows={checkedRows}
            setCheckedRows={setCheckedRows}
          />
        </div>
      )}
      {isDeleteModalOpened && (
        <DeleteModal
          data={data}
          setData={setData}
          setIsDeleteModalOpened={setIsDeleteModalOpened}
          onDeleted={handleDeleteConfirmed} />
      )}
    </div>
  );
};

export default ManageContacts;
